import {Dispatch} from "redux";

import {abTestingSelector} from "@pg-mono/ab-testing";
import {IFetchContext} from "@pg-mono/data-fetcher";
import {isPlainObject} from "@pg-mono/nodash";
import {appendQueryString, catch400, catch404, getRequest} from "@pg-mono/request";
import {createRequestActionTypes} from "@pg-mono/request-state";
import {apiV2ListLink, Scenario} from "@pg-mono/rp-api-routes";

import {DSA_RATING_SORT_AB_TEST_ID, isDsaRatingSortTreatmentVariant} from "../../../ab_testing/dsa_rating_sort_ab_test";
import {IRPStore} from "../../../app/rp_reducer";
import {IRPRequestMeta} from "../../../app/rp_request_meta";
import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {redirectOnList404} from "../../helpers/friendly_offer_list/redirect_on_list_404";
import {OfferType} from "../../types/OfferType";
import {OFFER_LIST_PAGE_SIZE, UNAVAILABLE_OFFERS_THRESHOLD} from "../constants/offer_list";
import {getFetchOfferListPropertiesCountQuery, getFetchOfferListQueryData} from "../helpers/get_fetch_offer_list_query_data";
import {isPromotionsListingQuery} from "../helpers/map_offer_list_promotions_query_param";
import {IOfferListResponse} from "../types/IOfferListResponse";
import {fetchOfferListPromotionalOffersCount, fetchOfferListPromotionalOffersCountTypes} from "./fetch_offer_list_promotional_offers_count";
import {fetchPrivilegedOfferList} from "./fetch_privileged_offer_list";
import {fetchPropertiesCount} from "./fetch_properties_count";
import {fetchSoonFinishedOfferList, resetSoonFinishedOfferList} from "./fetch_soon_finished_offer_list";
import {fetchUnavailableOfferList, resetUnavailableOfferList} from "./fetch_unavailable_offer_list";

const OFFER_LIST = "offer_list/fetch";
export const fetchOfferListTypes = createRequestActionTypes(OFFER_LIST);

const offerListLink = apiV2ListLink.offer.list(Scenario.OFFER_LIST);

const syncListingContextPromotionalOffersCountFromPromotionsListing = (dispatch: Dispatch, query: Record<string, string | string[] | null>, count: number) => {
    if (isPromotionsListingQuery(query)) {
        dispatch({type: fetchOfferListPromotionalOffersCountTypes.success, result: count});
    }
};

// TODO: consider creating one interface for offer list query instead of using `Record` containing different types. ILatestQuery may be a good starting point
export const fetchOfferList =
    (ctx: IFetchContext<IRPRequestMeta>, query: Record<string, string | string[] | null>) => async (dispatch: Dispatch, getState: () => IRPStore) => {
        dispatch({type: fetchOfferListTypes.start, latestQuery: query});

        const useDsaSort = isDsaRatingSortTreatmentVariant(abTestingSelector(DSA_RATING_SORT_AB_TEST_ID)(getState()));
        const {fullQuery, paramPage, safeQuery} = getFetchOfferListQueryData(query, true, useDsaSort);
        const url = appendQueryString(offerListLink, fullQuery);

        // Count-only fetch starts in parallel with the active list — strip `page` since page_size=0 has no pages
        const {page: _page, ...fullQueryWithoutPage} = fullQuery;
        const activeCountPromise = (
            getRequest(ctx.meta, appendQueryString(offerListLink, {...fullQueryWithoutPage, page_size: 0})) as Promise<IOfferListResponse>
        ).catch((error) => {
            notifyBugsnag(error, "Failed to fetch active offers count", {query});
            return null;
        });

        // As soon as count resolves, start unavailable fetch in parallel with the still-running active list
        const unavailableFetchPromise = activeCountPromise.then((countResp) => {
            const count = countResp?.count ?? null;
            if (count !== null && count < UNAVAILABLE_OFFERS_THRESHOLD) {
                return dispatch(fetchUnavailableOfferList(ctx, paramPage, count, query)) as unknown as Promise<void>;
            }
            return null;
        });

        const [, , offerListResponse] = await Promise.all([
            dispatch(fetchPropertiesCount(ctx, getFetchOfferListPropertiesCountQuery(query))),
            dispatch(fetchOfferListPromotionalOffersCount(ctx, fullQuery, query)),
            (getRequest(ctx.meta, url) as Promise<IOfferListResponse>)
                .then((response: IOfferListResponse) => {
                    const result = {
                        offers: response.results,
                        count: response.count,
                        page_size: response.page_size,
                        page: fullQuery.page
                    };
                    dispatch({type: fetchOfferListTypes.success, result});
                    syncListingContextPromotionalOffersCountFromPromotionsListing(dispatch, query, response.count);
                    return response;
                })
                .catch(
                    catch400((error) => {
                        dispatch({type: fetchOfferListTypes.error, error: error.appError});
                    })
                )
                .catch(
                    catch404(async () => {
                        const countResp = await activeCountPromise;
                        const count = countResp?.count ?? null;
                        if (count === null || count >= UNAVAILABLE_OFFERS_THRESHOLD) {
                            return dispatch(redirectOnList404(ctx.route, paramPage, ctx.meta));
                        }

                        // Short listing — active 404'd because we're on an unavailable-only page
                        const result = {offers: [], count, page_size: OFFER_LIST_PAGE_SIZE, page: paramPage};
                        dispatch({type: fetchOfferListTypes.success, result});
                        syncListingContextPromotionalOffersCountFromPromotionsListing(dispatch, query, count);
                        return {count, results: []} as unknown as IOfferListResponse;
                    })
                )
                .then(async (prevResponse: IOfferListResponse | boolean | void) => {
                    if (isPlainObject(prevResponse)) {
                        const response = prevResponse as IOfferListResponse;
                        const wereOffersFilteredByFinishedParam = query.construction_end_date === "0";
                        const wereThereNoOffersToShow = response.results.length === 0;

                        if (wereOffersFilteredByFinishedParam && wereThereNoOffersToShow) {
                            return dispatch(
                                fetchSoonFinishedOfferList(ctx, paramPage, {
                                    ...query,
                                    ...(safeQuery.type == null ? {type: [OfferType.FLAT, OfferType.HOUSE]} : {})
                                })
                            );
                        }
                    }

                    dispatch(resetSoonFinishedOfferList());
                    return prevResponse;
                })
                .then(async (prevResponse: IOfferListResponse | boolean | void) => {
                    const countResp = await activeCountPromise;
                    const count = countResp?.count ?? null;
                    if (count === null || count >= UNAVAILABLE_OFFERS_THRESHOLD) {
                        dispatch(resetUnavailableOfferList());
                    }
                    return prevResponse;
                }),
            dispatch(fetchPrivilegedOfferList(ctx)),
            unavailableFetchPromise
        ]);

        return offerListResponse;
    };
import {Dispatch} from "redux";

import {abTestingSelector} from "@pg-mono/ab-testing";
import {IFetchContext} from "@pg-mono/data-fetcher";
import {isPlainObject} from "@pg-mono/nodash";
import {appendQueryString, catch400, catch404, getRequest} from "@pg-mono/request";
import {createRequestActionTypes} from "@pg-mono/request-state";
import {apiV2ListLink, Scenario} from "@pg-mono/rp-api-routes";

import {DSA_RATING_SORT_AB_TEST_ID, isDsaRatingSortTreatmentVariant} from "../../../ab_testing/dsa_rating_sort_ab_test";
import {IRPStore} from "../../../app/rp_reducer";
import {IRPRequestMeta} from "../../../app/rp_request_meta";
import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {redirectOnList404} from "../../helpers/friendly_offer_list/redirect_on_list_404";
import {OfferType} from "../../types/OfferType";
import {OFFER_LIST_PAGE_SIZE, UNAVAILABLE_OFFERS_THRESHOLD} from "../constants/offer_list";
import {getFetchOfferListPropertiesCountQuery, getFetchOfferListQueryData} from "../helpers/get_fetch_offer_list_query_data";
import {isPromotionsListingQuery} from "../helpers/map_offer_list_promotions_query_param";
import {IOfferListResponse} from "../types/IOfferListResponse";
import {fetchOfferListPromotionalOffersCount, fetchOfferListPromotionalOffersCountTypes} from "./fetch_offer_list_promotional_offers_count";
import {fetchPrivilegedOfferList} from "./fetch_privileged_offer_list";
import {fetchPropertiesCount} from "./fetch_properties_count";
import {fetchSoonFinishedOfferList, resetSoonFinishedOfferList} from "./fetch_soon_finished_offer_list";
import {fetchUnavailableOfferList, resetUnavailableOfferList} from "./fetch_unavailable_offer_list";

const OFFER_LIST = "offer_list/fetch";
export const fetchOfferListTypes = createRequestActionTypes(OFFER_LIST);

const offerListLink = apiV2ListLink.offer.list(Scenario.OFFER_LIST);

const syncListingContextPromotionalOffersCountFromPromotionsListing = (dispatch: Dispatch, query: Record<string, string | string[] | null>, count: number) => {
    if (isPromotionsListingQuery(query)) {
        dispatch({type: fetchOfferListPromotionalOffersCountTypes.success, result: count});
    }
};

// TODO: consider creating one interface for offer list query instead of using `Record` containing different types. ILatestQuery may be a good starting point
export const fetchOfferList =
    (ctx: IFetchContext<IRPRequestMeta>, query: Record<string, string | string[] | null>) => async (dispatch: Dispatch, getState: () => IRPStore) => {
        dispatch({type: fetchOfferListTypes.start, latestQuery: query});

        const useDsaSort = isDsaRatingSortTreatmentVariant(abTestingSelector(DSA_RATING_SORT_AB_TEST_ID)(getState()));
        const {fullQuery, paramPage, safeQuery} = getFetchOfferListQueryData(query, true, useDsaSort);
        const url = appendQueryString(offerListLink, fullQuery);

        // Count-only fetch starts in parallel with the active list — strip `page` since page_size=0 has no pages
        const {page: _page, ...fullQueryWithoutPage} = fullQuery;
        const activeCountPromise = (
            getRequest(ctx.meta, appendQueryString(offerListLink, {...fullQueryWithoutPage, page_size: 0})) as Promise<IOfferListResponse>
        ).catch((error) => {
            notifyBugsnag(error, "Failed to fetch active offers count", {query});
            return null;
        });

        // As soon as count resolves, start unavailable fetch in parallel with the still-running active list
        const unavailableFetchPromise = activeCountPromise.then((countResp) => {
            const count = countResp?.count ?? null;
            if (count !== null && count < UNAVAILABLE_OFFERS_THRESHOLD) {
                return dispatch(fetchUnavailableOfferList(ctx, paramPage, count, query)) as unknown as Promise<void>;
            }
            return null;
        });

        const [, , offerListResponse] = await Promise.all([
            dispatch(fetchPropertiesCount(ctx, getFetchOfferListPropertiesCountQuery(query))),
            dispatch(fetchOfferListPromotionalOffersCount(ctx, fullQuery, query)),
            (getRequest(ctx.meta, url) as Promise<IOfferListResponse>)
                .then((response: IOfferListResponse) => {
                    const result = {
                        offers: response.results,
                        count: response.count,
                        page_size: response.page_size,
                        page: fullQuery.page
                    };
                    dispatch({type: fetchOfferListTypes.success, result});
                    syncListingContextPromotionalOffersCountFromPromotionsListing(dispatch, query, response.count);
                    return response;
                })
                .catch(
                    catch400((error) => {
                        dispatch({type: fetchOfferListTypes.error, error: error.appError});
                    })
                )
                .catch(
                    catch404(async () => {
                        const countResp = await activeCountPromise;
                        const count = countResp?.count ?? null;
                        if (count === null || count >= UNAVAILABLE_OFFERS_THRESHOLD) {
                            return dispatch(redirectOnList404(ctx.route, paramPage, ctx.meta));
                        }

                        // Short listing — active 404'd because we're on an unavailable-only page
                        const result = {offers: [], count, page_size: OFFER_LIST_PAGE_SIZE, page: paramPage};
                        dispatch({type: fetchOfferListTypes.success, result});
                        syncListingContextPromotionalOffersCountFromPromotionsListing(dispatch, query, count);
                        return {count, results: []} as unknown as IOfferListResponse;
                    })
                )
                .then(async (prevResponse: IOfferListResponse | boolean | void) => {
                    if (isPlainObject(prevResponse)) {
                        const response = prevResponse as IOfferListResponse;
                        const wereOffersFilteredByFinishedParam = query.construction_end_date === "0";
                        const wereThereNoOffersToShow = response.results.length === 0;

                        if (wereOffersFilteredByFinishedParam && wereThereNoOffersToShow) {
                            return dispatch(
                                fetchSoonFinishedOfferList(ctx, paramPage, {
                                    ...query,
                                    ...(safeQuery.type == null ? {type: [OfferType.FLAT, OfferType.HOUSE]} : {})
                                })
                            );
                        }
                    }

                    dispatch(resetSoonFinishedOfferList());
                    return prevResponse;
                })
                .then(async (prevResponse: IOfferListResponse | boolean | void) => {
                    const countResp = await activeCountPromise;
                    const count = countResp?.count ?? null;
                    if (count === null || count >= UNAVAILABLE_OFFERS_THRESHOLD) {
                        dispatch(resetUnavailableOfferList());
                    }
                    return prevResponse;
                }),
            dispatch(fetchPrivilegedOfferList(ctx)),
            unavailableFetchPromise
        ]);

        return offerListResponse;
    };
